import { useMemo } from "react";
import type { MatchRow, ProfileBestsData } from "@shared/api";
import { aggregateMatches, type MatchAggregate } from "@shared/lib/matchAggregate";
import { ChartState } from "@features/charts/ChartState";

const SESSION_GAP_SECONDS = 45 * 60;
const WIN_THRESHOLD = 0.01;

interface SessionBest {
  start: number;
  end: number;
  matches: number;
  aggregate: MatchAggregate;
}

interface DayBest {
  day: string;
  start: number;
  aggregate: MatchAggregate;
}

interface Streaks {
  longestWin: number;
  longestLoss: number;
  currentWin: number;
  currentLoss: number;
}

interface RecordsData {
  matchCount: number;
  bestSessionByKd: SessionBest | null;
  bestSessionByKills: SessionBest | null;
  bestDayByKd: DayBest | null;
  bestDayByKills: DayBest | null;
  streaks: Streaks;
}

export function Records({ rows, bests }: { rows: MatchRow[] | null; bests: ProfileBestsData | null }) {
  const data = useMemo(() => (rows ? buildRecords(rows) : null), [rows]);

  return (
    <section className="records-section">
      <div className="section-heading">
        <h2>Personal bests</h2>
        {data && <span className="records-sub">{data.matchCount.toLocaleString()} tracked matches</span>}
      </div>
      {!data ? (
        <ChartState>Loading records…</ChartState>
      ) : data.matchCount === 0 ? (
        <ChartState>No tracked matches yet.</ChartState>
      ) : (
        <div className="record-grid">
          <CareerBestCard label="Most kills (round)" value={bests?.most_kills_round ?? null} sub="Career best" />
          <CareerBestCard label="Most kills (life)" value={bests?.most_kills_life ?? null} sub="Single-life record" />
          <CareerBestCard label="Most damage (round)" value={bests?.most_damage_round ?? null} sub="Career best" />
          <CareerBestCard label="Top score (round)" value={bests?.most_score_round ?? null} sub="Career best" />
          <CareerBestCard label="Longest killstreak" value={bests?.longest_killstreak ?? null} sub="All-time" />
          <CareerBestCard label="Longest kill" value={bests?.longest_kill_meters ?? null} suffix=" m" sub="Furthest gunfight kill" />
          <CareerBestCard label="Biggest multikill" value={bests?.biggest_multikill ?? null} sub="Highest combo" />
          <CareerBestCard label="Most headshots (life)" value={bests?.most_headshots_life ?? null} sub="Single-life record" />
          <CareerBestCard label="Most assists (round)" value={bests?.most_assists_round ?? null} sub="Career best" />
          <CareerBestCard label="Most revives (round)" value={bests?.most_revives_round ?? null} sub="Career best" />
          <CareerBestCard label="Vehicles destroyed (round)" value={bests?.most_vehicles_destroyed_round ?? null} sub="Career best" />
          <CareerBestCard label="Squad wipes (round)" value={bests?.most_squad_wipes_round ?? null} sub="Career best" />
          <StreakCard label="Win streak" streak={data.streaks.longestWin} current={data.streaks.currentWin} tone="good" />
          <StreakCard label="Loss streak" streak={data.streaks.longestLoss} current={data.streaks.currentLoss} tone="bad" />
          <SessionRecordCard label="Best session K/D" session={data.bestSessionByKd} metric="kd" />
          <SessionRecordCard label="Top session kills" session={data.bestSessionByKills} metric="kills" />
          <DayRecordCard label="Best day K/D" day={data.bestDayByKd} metric="kd" />
          <DayRecordCard label="Top day kills" day={data.bestDayByKills} metric="kills" />
        </div>
      )}
    </section>
  );
}

function CareerBestCard({ label, value, suffix, sub }: { label: string; value: number | null; suffix?: string; sub: string }) {
  if (value === null || !Number.isFinite(value)) {
    return (
      <article className="record-card tone-accent">
        <span>{label}</span>
        <strong>—</strong>
        <p>Not yet reported.</p>
      </article>
    );
  }
  return (
    <article className="record-card tone-accent">
      <span>{label}</span>
      <strong>
        {value.toLocaleString()}
        {suffix ?? ""}
      </strong>
      <p>{sub}</p>
    </article>
  );
}

function StreakCard({ label, streak, current, tone }: { label: string; streak: number; current: number; tone: "good" | "bad" }) {
  return (
    <article className={`record-card tone-${tone}`}>
      <span>{label}</span>
      <strong>{streak.toLocaleString()}</strong>
      <p>
        {streak === 0 ? "None recorded yet." : `Longest run of consecutive rows.${current > 0 ? ` Current: ${current}.` : ""}`}
      </p>
    </article>
  );
}

function SessionRecordCard({ label, session, metric }: { label: string; session: SessionBest | null; metric: "kd" | "kills" }) {
  if (!session) {
    return (
      <article className="record-card tone-accent">
        <span>{label}</span>
        <strong>—</strong>
        <p>Need at least one full session.</p>
      </article>
    );
  }
  const headline = metric === "kd" ? (session.aggregate.kd ?? 0).toFixed(2) : session.aggregate.kills.toLocaleString();
  const when = new Date(session.end * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  const minutes = Math.round(session.aggregate.activeSeconds / 60);
  return (
    <article className="record-card tone-accent">
      <span>{label}</span>
      <strong>{headline}</strong>
      <p>
        {when} · {session.matches} matches · {minutes}m · {session.aggregate.wins}W/{session.aggregate.losses}L
      </p>
    </article>
  );
}

function DayRecordCard({ label, day, metric }: { label: string; day: DayBest | null; metric: "kd" | "kills" }) {
  if (!day) {
    return (
      <article className="record-card tone-accent">
        <span>{label}</span>
        <strong>—</strong>
        <p>No days with tracked matches.</p>
      </article>
    );
  }
  const headline = metric === "kd" ? (day.aggregate.kd ?? 0).toFixed(2) : day.aggregate.kills.toLocaleString();
  const when = new Date(day.start * 1000).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  return (
    <article className="record-card tone-accent">
      <span>{label}</span>
      <strong>{headline}</strong>
      <p>
        {when} · {day.aggregate.observedMatches} matches · {day.aggregate.wins}W/{day.aggregate.losses}L
      </p>
    </article>
  );
}

function buildRecords(rows: MatchRow[]): RecordsData {
  const sortedAsc = [...rows].sort((a, b) => a.window_end - b.window_end);

  const sessions = buildSessions(sortedAsc);
  const bestSessionByKd = sessions
    .filter((s) => s.matches >= 2 && (s.aggregate.kd ?? 0) > 0)
    .sort((a, b) => (b.aggregate.kd ?? 0) - (a.aggregate.kd ?? 0))[0] ?? null;
  const bestSessionByKills = sessions
    .filter((s) => s.aggregate.kills > 0)
    .sort((a, b) => b.aggregate.kills - a.aggregate.kills)[0] ?? null;

  const days = buildDays(sortedAsc);
  const bestDayByKd = days
    .filter((d) => d.aggregate.observedMatches >= 2 && (d.aggregate.kd ?? 0) > 0)
    .sort((a, b) => (b.aggregate.kd ?? 0) - (a.aggregate.kd ?? 0))[0] ?? null;
  const bestDayByKills = days
    .filter((d) => d.aggregate.kills > 0)
    .sort((a, b) => b.aggregate.kills - a.aggregate.kills)[0] ?? null;

  const streaks = buildStreaks(sortedAsc);

  return {
    matchCount: sortedAsc.reduce((sum, row) => sum + row.merged_match_count, 0),
    bestSessionByKd,
    bestSessionByKills,
    bestDayByKd,
    bestDayByKills,
    streaks,
  };
}

function buildSessions(rowsAsc: MatchRow[]): SessionBest[] {
  if (rowsAsc.length === 0) return [];
  const sessions: SessionBest[] = [];
  let bucket: MatchRow[] = [];
  let lastEnd: number | null = null;
  for (const row of rowsAsc) {
    if (lastEnd !== null && row.window_end - lastEnd > SESSION_GAP_SECONDS) {
      sessions.push(summarize(bucket));
      bucket = [];
    }
    bucket.push(row);
    lastEnd = row.window_end;
  }
  if (bucket.length > 0) sessions.push(summarize(bucket));
  return sessions;
}

function summarize(bucket: MatchRow[]): SessionBest {
  const aggregate = aggregateMatches(bucket);
  return {
    start: Math.min(...bucket.map((r) => r.window_start)),
    end: Math.max(...bucket.map((r) => r.window_end)),
    matches: aggregate.observedMatches,
    aggregate,
  };
}

function buildDays(rowsAsc: MatchRow[]): DayBest[] {
  const buckets = new Map<string, { start: number; rows: MatchRow[] }>();
  for (const row of rowsAsc) {
    const date = new Date(row.window_end * 1000);
    date.setHours(0, 0, 0, 0);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    const entry = buckets.get(key) ?? { start: Math.floor(date.getTime() / 1000), rows: [] };
    entry.rows.push(row);
    buckets.set(key, entry);
  }
  return [...buckets.entries()].map(([day, entry]) => ({ day, start: entry.start, aggregate: aggregateMatches(entry.rows) }));
}

function buildStreaks(rowsAsc: MatchRow[]): Streaks {
  let longestWin = 0;
  let longestLoss = 0;
  let runWin = 0;
  let runLoss = 0;
  for (const row of rowsAsc) {
    const outcome = rowOutcome(row);
    if (outcome === "win") {
      runWin++;
      runLoss = 0;
      if (runWin > longestWin) longestWin = runWin;
    } else if (outcome === "loss") {
      runLoss++;
      runWin = 0;
      if (runLoss > longestLoss) longestLoss = runLoss;
    } else {
      runWin = 0;
      runLoss = 0;
    }
  }
  return {
    longestWin,
    longestLoss,
    currentWin: runWin,
    currentLoss: runLoss,
  };
}

function rowOutcome(row: MatchRow): "win" | "loss" | "none" {
  if (row.wins_delta >= 1 - WIN_THRESHOLD) return "win";
  if (row.losses_delta >= 1 - WIN_THRESHOLD) return "loss";
  return "none";
}
